<template>
  <transition-group
    v-if="shouldShow"
    class="s-chips-display"
    :class="[`s-chips-display--${variant}`]"
    name="s-chips-display"
    tag="div"
  >
    <component
      :is="tag"
      v-for="{ value, label } in items"
      :key="value"
      class="s-chips-display__item"
      :class="{ 's-chips-display__item--removable': removable }"
      :type="tag === 'button' ? 'button' : null"
      @click.stop="removeValue(value)"
    >
      {{ label }}
      <i
        v-if="removable"
        class="s-chips-display__item-icon fa-regular fa-xmark"
      ></i>
    </component>
  </transition-group>
</template>

<script
  setup
  lang="ts"
  generic="Item extends UseMultiValueDisplayGeneric['item']"
>
import type {
  UseMultiValueDisplayEmit,
  UseMultiValueDisplayGeneric,
} from "@/lib/components/logic/atoms/useMultiValueDisplay";

import * as useMultiValueDisplay from "@/lib/components/logic/atoms/useMultiValueDisplay";
const props = defineProps(useMultiValueDisplay.props<Item>());
const emit = defineEmits<UseMultiValueDisplayEmit<Item>>();

const { shouldShow, items, removeValue, removable, tag, variant } =
  useMultiValueDisplay.use(props, emit);
</script>

<style lang="postcss">
.s-chips-display {
  @apply relative flex flex-wrap gap-x-3 gap-y-2;

  &__item {
    @apply flex h-6 items-center rounded leading-none transition-[background-color,color] duration-150;

    &--removable:hover {
      @apply cursor-pointer;
    }

    &-icon {
      @apply ml-1 h-3 w-3 rounded-full p-0.5;
    }
  }

  &--opaque &__item {
    @apply bg-primary-50 px-2 text-sm text-primary-800;

    &--removable:hover {
      @apply bg-primary-100;

      .s-chips-display__item-icon {
        @apply text-primary-500;
      }
    }

    &-icon {
      @apply text-primary-300;
    }
  }

  &--clear &__item {
    @apply text-slate-500;

    &--removable:hover {
      @apply cursor-pointer text-slate-700;

      .s-chips-display__item-icon {
        @apply text-slate-700;
      }
    }

    &-icon {
      @apply text-slate-400;
    }
  }

  &-move,
  &-enter-active,
  &-leave-active {
    @apply transition-all duration-500 ease-in-out;
  }

  &-enter-from,
  &-leave-to {
    @apply opacity-0;
  }

  &-leave-active {
    @apply absolute;
  }
}
</style>
